import React from 'react'
import { Minus, Plus, Trash, Package, Gift, Heart } from '@phosphor-icons/react'
import { IconButton, Icon, getTextColorByBrightness } from '@shopflo/ui'
import { customizeCartUIType } from 'src/lib/types/cartUI'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { QuickAddOnAddonLevel } from 'src/lib/types/quickAddOnV2'
import { ShopfloImageDomain } from 'src/lib/utils/constants'
import { PreviewCartItem } from 'src/lib/contexts/CartLayoutContext'

interface CartItem {
  id: string
  title: string
  variant: string
  price: number
  originalPrice: number
  quantity: number
  is_freebie?: boolean
}

const staticCartItems: CartItem[] = [
  {
    id: '1',
    title: 'Premium Cotton T-Shirt',
    variant: 'Size: M, Color: Blue',
    price: 200,
    originalPrice: 1499,
    quantity: 0
  },
  {
    id: '3',
    title: 'Running Shoes Pro',
    variant: 'Size: 10, Color: Black',
    price: 500,
    originalPrice: 3499,
    quantity: 0
  },
  {
    id: '2',
    title: 'Free Gift - Keychain',
    variant: 'One Size',
    price: 0,
    originalPrice: 500,
    quantity: 1,
    is_freebie: true
  }
]

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

const AddOnItem: React.FC<{ addon: any; uiConfig?: customizeCartUIType['orderSummaryConfig'] }> = ({
  addon,
  uiConfig
}) => {
  const productName = addon.metadata?.product?.[0]?.description || 'Add-on'
  const price = addon.metadata?.product?.[0]?.variants?.[0]?.price || 0
  const image = addon.metadata?.product?.[0]?.imageUrl

  return (
    <div className='flex flex-row gap-3 pl-2'>
      <div className='relative flex items-center gap-3'>
        <div className='relative'>
          <div className='h-10 w-10 rounded-lg flex items-center justify-center'>
            <img
              src={image || `${ShopfloImageDomain}/package.png`}
              alt={productName}
              className='h-full w-full rounded-lg object-cover'
            />
          </div>
          <button
            className='absolute -left-2 -top-[7.5px] flex items-center justify-center rounded-md p-1'
            style={{
              backgroundColor: uiConfig?.textColor || '#27272a',
              boxShadow: `0px 0px 0px 1px ${hexToRGBA(uiConfig?.backgroundColor || '#FFFFFF', 0.15)}`
            }}
          >
            <Plus weight='bold' className='h-2.5 w-2.5' style={{ color: uiConfig?.backgroundColor || '#FFFFFF' }} />
          </button>
        </div>

        <div className='flex flex-row items-center gap-5 flex-1'>
          <div className='flex flex-col gap-1 flex-1'>
            <div className='flex flex-col gap-0'>
              <p
                className='text-xs font-medium leading-[1.5em] tracking-[-0.01em]'
                style={{
                  color: uiConfig?.textColor
                }}
              >
                {productName}
              </p>
              <div className='flex flex-row items-center gap-1.5 cursor-pointer'>
                <div className='flex flex-row items-center justify-center gap-1 rounded-lg py-0.5'>
                  <div className='flex flex-row items-center gap-0.5 space-x-1'>
                    <span
                      className='text-text-xs-semibold leading-[1.57em] tracking-[-0.01em] text-center underline decoration-1 underline-offset-2'
                      style={{
                        color: uiConfig?.textColor,
                        textDecorationColor: getTextColorByBrightness({
                          bgHexColor: uiConfig?.backgroundColor ?? '#FFFFFF'
                        })
                      }}
                    >
                      Add
                    </span>
                    <span
                      className='text-xs font-medium leading-[1.5em] tracking-[-0.01em] text-center'
                      style={{
                        color: uiConfig?.textColor,
                        opacity: '60%'
                      }}
                    >
                      {formatCurrency(price)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SingleItemCard: React.FC<{
  item: CartItem
  uiConfig?: customizeCartUIType['orderSummaryConfig']
  quickAddOnTable: any[]
  cartItems: PreviewCartItem[]
  updateCartItem: (item: PreviewCartItem) => void
  wishlistConfig?: {
    enabled: boolean
    primaryColor: string
  }
}> = ({ item, uiConfig, quickAddOnTable, cartItems: contextCartItems, updateCartItem, wishlistConfig }) => {
  const { title, variant, price, originalPrice, quantity, is_freebie } = item

  const hasDiscount = originalPrice > price && !is_freebie

  const contextItem = contextCartItems.find(ci => ci.id === item.id)
  const currentQuantity = contextItem?.quantity ?? quantity

  const finalItemPrice = price * currentQuantity
  const finalCompareAtPrice = originalPrice * currentQuantity

  // Preview only ever shows one add-on widget per item, matching the storefront (only one
  // item-level add-on ever renders per item there too), regardless of how many are configured.
  const itemLevelAddons = quickAddOnTable
    .filter(addon => addon.addonLevel === QuickAddOnAddonLevel.ITEM && addon.status === true)
    .slice(0, 1)

  const handleIncrement = () => {
    updateCartItem({ ...item, quantity: currentQuantity + 1 })
  }

  const handleDecrement = () => {
    if (currentQuantity > 0) {
      updateCartItem({ ...item, quantity: currentQuantity - 1 })
    }
  }

  return (
    <li>
      <div className='flex w-full min-w-full flex-row justify-between transition-all duration-300 relative z-2 rounded-xl'>
        <div className='flex w-full space-x-3 pr-1'>
          <div className='relative flex items-center justify-center min-h-[72px] min-w-[72px]'>
            {is_freebie ? (
              <div
                className='absolute top-[2px] right-[2px] flex h-6 w-6 items-center justify-center rounded-md rounded-tl-none rounded-br-none shadow-sm z-10'
                style={{
                  backgroundColor: uiConfig?.offerBadgeColor
                }}
              >
                <Icon IconComponent={Gift} size='xxs' iconProps={{ className: 'text-white' }} />
              </div>
            ) : null}
            <div className='overflow-hidden border border-zinc-200 h-[72px] w-[72px] rounded-lg bg-zinc-200 animate-pulse' />
          </div>
          <div className='flex grow flex-col items-center justify-start min-h-[72px]'>
            <div className='flex w-full justify-between items-center gap-2 min-h-[36px]'>
              <div>
                <div className='flex w-full flex-row items-start justify-between space-x-3'>
                  <p
                    className='overflow-hidden text-ellipsis text-text-xs-medium text-zinc-700 line-clamp-2 text-wrap'
                    style={{
                      color: uiConfig?.textColor
                    }}
                  >
                    {title}
                  </p>
                </div>
                <div
                  className='w-full overflow-hidden text-ellipsis text-text-xs-regular text-zinc-500'
                  style={{
                    color: uiConfig?.textColor,
                    opacity: '60%'
                  }}
                >
                  {variant}
                </div>
              </div>
              <div className='flex flex-col items-end'>
                {!is_freebie && (
                  <>
                    <span
                      className='text-text-sm-semibold text-zinc-700'
                      style={{
                        color: uiConfig?.textColor
                      }}
                    >
                      {formatCurrency(finalItemPrice)}
                    </span>
                    {hasDiscount && (
                      <span
                        className='text-gray-dark line-through text-xxs'
                        style={{
                          color: uiConfig?.textColor,
                          opacity: '40%'
                        }}
                      >
                        {formatCurrency(finalCompareAtPrice)}
                      </span>
                    )}
                  </>
                )}
                {is_freebie && (
                  <span className='flex flex-row items-end space-x-1'>
                    <span className='flex text-sm items-end flex-col-reverse'>
                      <span className='text-gray-dark line-through text-xxs'>
                        {formatCurrency(finalCompareAtPrice)}
                      </span>
                      <span className='text-text-sm-semibold'>
                        <span
                          className='text-yay-dark'
                          style={{
                            color: uiConfig?.offerBadgeColor
                          }}
                        >
                          FREE!
                        </span>
                      </span>
                    </span>
                  </span>
                )}
              </div>
            </div>
            {!is_freebie && (
              <div className='flex w-full flex-row items-center space-x-2 pt-1'>
                <IconButton
                  IconComponent={Trash}
                  variant='outline'
                  size='xs'
                  className='!bg-transparent h-7 w-7'
                  style={
                    {
                      '--before-border-color': getTextColorByBrightness({
                        bgHexColor: uiConfig?.backgroundColor ?? '#FFFFFF'
                      }),
                      '--before-border-color-hover': getTextColorByBrightness({
                        bgHexColor: uiConfig?.backgroundColor ?? '#FFFFFF',
                        darkOpacity: 24
                      })
                    } as React.CSSProperties
                  }
                  iconProps={{
                    weight: 'bold',
                    className: 'text-zinc-400 h-3.5 w-3.5',
                    color: getTextColorByBrightness({
                      bgHexColor: uiConfig?.backgroundColor ?? '#FFFFFF',
                      darkOpacity: 50,
                      lightOpacity: 50
                    })
                  }}
                />

                <div
                  className='flex h-7 p-0 w-[72px] flex-row items-center rounded border border-gray-light overflow-hidden'
                  style={{
                    backgroundColor: uiConfig?.quantityEditorBackground,
                    borderColor: getTextColorByBrightness({
                      bgHexColor: uiConfig?.quantityEditorBackground ?? '#FFFFFF',
                      lightOpacity: 24
                    })
                  }}
                >
                  <IconButton
                    IconComponent={Minus}
                    variant='ghost'
                    className='h-full w-8'
                    size='xxs'
                    onClick={handleDecrement}
                    iconProps={{
                      weight: 'bold',
                      className: 'text-zinc-500',
                      color: uiConfig?.quantityEditorText,
                      opacity: '60%'
                    }}
                  />
                  <p
                    className='grow basis-1/3 text-center text-xs font-medium text-coal-dark'
                    style={{
                      color: uiConfig?.quantityEditorText
                    }}
                  >
                    {currentQuantity}
                  </p>
                  <IconButton
                    IconComponent={Plus}
                    className='h-full w-8'
                    variant='ghost'
                    size='xxs'
                    onClick={handleIncrement}
                    iconProps={{
                      weight: 'bold',
                      className: 'text-zinc-500',
                      color: uiConfig?.quantityEditorText,
                      opacity: '60%'
                    }}
                  />
                </div>
                {wishlistConfig?.enabled && (
                  <Icon
                    IconComponent={Heart}
                    size='sm'
                    iconProps={{
                      weight: wishlistConfig?.enabled ? 'fill' : 'regular',
                      color: wishlistConfig?.primaryColor
                    }}
                    onClick={event => {
                      event.stopPropagation()
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!is_freebie && itemLevelAddons && itemLevelAddons.length > 0 && (
        <div className='pl-8 pt-3 space-y-3'>
          {itemLevelAddons.map(addon => (
            <AddOnItem key={addon.id} addon={addon} uiConfig={uiConfig} />
          ))}
        </div>
      )}
    </li>
  )
}

interface ItemCardProps {
  orderSummaryConfig: customizeCartUIType['orderSummaryConfig']
  quickAddOnTable: any[]
  cartItems: PreviewCartItem[]
  updateCartItem: (item: PreviewCartItem) => void
  wishlistConfig?: {
    enabled: boolean
    primaryColor: string
  }
}

export const ItemCard: React.FC<ItemCardProps> = ({
  orderSummaryConfig,
  quickAddOnTable,
  cartItems: contextCartItems,
  updateCartItem,
  wishlistConfig
}) => {
  const borderStyles =
    orderSummaryConfig.cardBorderSides === 'bottom'
      ? {
          borderBottomWidth: orderSummaryConfig.cardBorderWidth,
          borderBottomColor: orderSummaryConfig.borderColor,
          borderBottomStyle: 'solid' as const
        }
      : {
          borderWidth: orderSummaryConfig.cardBorderWidth,
          borderColor: orderSummaryConfig.borderColor,
          borderStyle: 'solid' as const
        }

  return (
    <ul
      className="flex flex-col gap-5 bg-zinc-100 rounded-xl p-[10px] [&>li]:relative [&>li:not(:last-child)]:after:content-[''] [&>li:not(:last-child)]:after:absolute [&>li:not(:last-child)]:after:left-0 [&>li:not(:last-child)]:after:right-0 [&>li:not(:last-child)]:after:bottom-[-10px] [&>li:not(:last-child)]:after:h-[0.5px] [&>li:not(:last-child)]:after:bg-[var(--divider-color)]"
      style={{
        backgroundColor: orderSummaryConfig.backgroundColor,
        borderRadius: orderSummaryConfig.cardRadius,
        ...borderStyles,
        ['--divider-color' as any]: getTextColorByBrightness({
          bgHexColor: orderSummaryConfig.backgroundColor ?? '#FFFFFF',
          lightOpacity: 24
        })
      }}
    >
      {staticCartItems.map(item => (
        <SingleItemCard
          wishlistConfig={wishlistConfig}
          key={item.id}
          item={item}
          uiConfig={orderSummaryConfig}
          quickAddOnTable={quickAddOnTable}
          cartItems={contextCartItems}
          updateCartItem={updateCartItem}
        />
      ))}
    </ul>
  )
}
